import { getHomePlanCustomerId, HOME_PLAN_CATEGORY_LABELS } from "api-types/homePlans/homePlanFields";
import type { ClientHomePlan, ClientHomePlanHydratedNoDoc } from "api-types/homePlans/homePlanResponses";
import type { HomePlanCategory } from "api-types/homePlans/homePlanTypes";
import type { ClientLocationPlan } from "api-types/locationPlans/locationPlansResponses";
import type { ClientLocationWithLevel } from "api-types/locations/locationBaseTypes";
import { isMultiUnitLocation } from "api-types/locations/locationDisplay";
import type { FetchErrorResponse } from "common/utils/fetchHelpers";
import { snakeCaseToTitleCase } from "common/utils/strings";
import { useHomePlanLockUnlockState } from "lib/components/builders/communities/plans/homePlans/homePlanLockUtils";
import EditHomePlanModal from "lib/components/builders/communities/plans/modals/CreateOrUpdateHomePlanModal";
import useHomePlanEvents from "lib/components/builders/communities/utils/useHomePlanEvents";
import IconHomePlanCategory from "lib/components/typedIcons/IconHomePlanCategory";
import {
  CONFIGURE_OPERATIONS_MULTI_UNIT_TOOLTIP,
  DELETE_HOME_PLAN_MESSAGE,
  DELETE_HOME_PLAN_MULTI_UNIT_MESSAGE,
  DETACH_MULTI_UNIT_HOME_PLAN_TOOLTIP,
} from "lib/constants/messages";
import { homePlanOpsViewPath, procurePath, studioPath, useAppRouterParams } from "lib/routeHelpers";
import { userCanDetachHomePlan, userCanEditHomePlan, userCanMoveHomePlan, userCanUseStudio } from "lib/security/client";
import { useUserContext } from "lib/state/UserContextProvider";
import type { FormEvent } from "react";
import type { ReactNode } from "react";
import { useState } from "react";
import ConfirmDeleteModal from "ui/components/ConfirmDeleteModal";
import DropdownMenu, { DropdownMenuButton } from "ui/components/DropdownMenu";
import Link from "ui/components/Link";
import PopoverMenu from "ui/components/PopoverMenu";
import lazySuspense from "ui/hooks/lazySuspense";
import useAppContext from "ui/hooks/useAppContext";
import IconAddressBook from "ui/icons/IconAddressBook";
import IconCheck from "ui/icons/IconCheck";
import IconDetached from "ui/icons/IconDetached";
import IconDocSpreadsheet from "ui/icons/IconDocSpreadsheet";
import IconEditSquare from "ui/icons/IconEditSquare";
import IconFileOpen from "ui/icons/IconFileOpen";
import IconLineArrow from "ui/icons/IconLineArrow";
import IconLocked from "ui/icons/IconLocked";
import IconManagementView from "ui/icons/IconManagementView";
import IconSync from "ui/icons/IconSync";
import CustomerInfoModal from "./CustomerInfoModal";
import DetachHomePlanModal from "./DetachHomePlanModal";
import { DeleteHomePlanMenuLink, ShowroomMenuLink } from "./homePlanDisplayUtils";
import styles from "./HomePlanDropdown.module.scss";
import LockHomePlanButton from "./LockHomePlanButton";
import MoveHomePlanModal from "./moveHomePlans/MoveHomePlanModal";
import RefreshOptionDataModal from "./RefreshOptionDataModal";
import { useDeleteHomePlan, useUpdateHomePlan } from "./useHomePlanAPI";

const LockPlanModal = lazySuspense(() => import("./LockPlanModal"));

type SetModalFn = (arg0: boolean) => void;

export interface HomePlanDropdownModalState {
  openEditModal: boolean;
  setOpenEditModal: SetModalFn;
}

interface HomePlanDropdownProps {
  modalState: HomePlanDropdownModalState;
  setActiveHomePlan: (homePlan: ClientHomePlanHydratedNoDoc) => void;
  homePlan: ClientHomePlanHydratedNoDoc;
  location?: ClientLocationWithLevel;
  onUpdate: (homePlan: ClientHomePlanHydratedNoDoc | ClientHomePlan) => void;
  onDelete: (id: string) => void;
  hoverBlue?: boolean;
}

export function HomePlanDropdownModalState(): HomePlanDropdownModalState {
  const [openEditModal, setOpenEditModal] = useState(false);

  return {
    openEditModal,
    setOpenEditModal,
  };
}

type HomePlanDropdownModalScenario =
  | "detach"
  | "delete"
  | "customerInfo"
  | "unlock"
  | "lock"
  | "move"
  | "refreshOptions";

// TODO: determine why edit modal state is managed by parent?
export default function HomePlanDropdown(props: HomePlanDropdownProps) {
  const { builderId } = useAppRouterParams();

  if (!builderId) {
    return null;
  }

  return <ReadyHomePlanDropDown builderId={builderId} props={props} />;
}

function ReadyHomePlanDropDown({
  builderId,
  props: { modalState, homePlan, location, setActiveHomePlan, hoverBlue, onUpdate, onDelete },
}: {
  builderId: string;
  props: HomePlanDropdownProps;
}) {
  const [openMenu, setOpenMenu] = useState(false);
  const [modalScenario, setModalScenario] = useState<HomePlanDropdownModalScenario | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { setUserNotification } = useAppContext();
  const sendHomePlanEvent = useHomePlanEvents(homePlan);
  const deleteHomePlan = useDeleteHomePlan(builderId);
  const updateHomePlan = useUpdateHomePlan(builderId);

  const userContext = useUserContext();
  const { hasPermission, enabledFeatures } = userContext;
  const canViewProcure = hasPermission("VIEW_PROCURE");
  const canDeleteHomePlan = hasPermission("DELETE_HOME_PLANS");
  const canViewStudio = userCanUseStudio(userContext);
  const canEditHomePlan = userCanEditHomePlan(userContext, homePlan, location);
  const canViewOperationsMode = hasPermission("VIEW_OPERATIONS_MODE");
  const showMenu = canEditHomePlan || canViewProcure || canViewStudio || canDeleteHomePlan;

  /**
   * We are supporting both locking & detaching, with one limitation. If a home plan is locked, it cannot be detached.
   * Otherwise you are free to lock, detach at will. Warning detaching is a one way process. There is no re-attach.
   */
  const atMultiUnitLocation = isMultiUnitLocation(location);
  const { homePlanLockable, lockTooltip, homePlanUnlockable, unlockTooltip } = useHomePlanLockUnlockState({
    builderId,
    homePlan,
    location,
    userContext,
    fetchCascadeLockPreview:
      !!homePlan.isMultiUnit && (openMenu || modalScenario === "lock" || modalScenario === "unlock"),
  });
  const homePlanDetachable = userCanDetachHomePlan(userContext, homePlan, location);

  // Show a different delete message when the HomePlan may be aggregated
  const isOnLot = !!homePlan?.lotId;
  const showMultiUnitDeleteWarning = atMultiUnitLocation && isOnLot;

  const toggleModalScenario = (scenario: HomePlanDropdownModalScenario) => {
    setOpenMenu(false);
    setModalScenario((current) => (current === scenario ? null : scenario));
  };

  const handleSubmitDelete = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setIsDeleting(true);

    try {
      await deleteHomePlan.mutateAsync(homePlan.id);
      sendHomePlanEvent("config:deleted_homeplan");
      onDelete(homePlan.id);
    } catch (error) {
      setUserNotification(error as FetchErrorResponse);
      setIsDeleting(false);
      return;
    }

    setIsDeleting(false);
    toggleModalScenario("delete");
    setUserNotification({ message: `${homePlan?.planName || "Home Plan"} has been deleted.`, messageType: "success" });
  };

  const category = homePlan.category;
  const getIconDisplaySize = (c: HomePlanCategory | null): number => (category === c ? 10 : 0);

  const updateHomePlanCategory = async (newCategory: HomePlanCategory | null): Promise<void> => {
    const currentHomePlan = { ...homePlan };
    const newHomePlan = { ...homePlan, category: newCategory };

    try {
      await updateHomePlan.mutateAsync({ homePlanId: homePlan.id, fields: { category: newCategory ?? undefined } });

      setUserNotification({
        messageType: "success",
        message: newCategory
          ? `Home Plan categorized as ${snakeCaseToTitleCase(newCategory)}.`
          : "Home Plan uncategorized.",
      });
      onUpdate(newHomePlan);
    } catch (e) {
      setUserNotification({ messageType: "error", message: "Error updating Home Plan category." });
      onUpdate(currentHomePlan);
    }
  };

  const categoryOptions: ReactNode[] = [
    <Link
      key="category_none"
      className={`category category_none ${!category && "selected"}`}
      onClick={() => updateHomePlanCategory(null)}
    >
      None
      <IconCheck size={getIconDisplaySize(null)} />
    </Link>,
    ...(Object.entries(HOME_PLAN_CATEGORY_LABELS) as [HomePlanCategory, string][]).map(([value, label]) => (
      <Link
        key={`category_${value}`}
        className={`category category_${value} ${category === value && "selected"}`}
        onClick={() => updateHomePlanCategory(value)}
      >
        <IconHomePlanCategory category={value} />
        {label}
        <IconCheck size={getIconDisplaySize(value)} />
      </Link>
    )),
  ];

  const [canMoveHomePlan, cantMoveReason] = userCanMoveHomePlan(userContext, homePlan, "HOME_PLANS");

  // Remove "None" selection once categorized
  if (homePlan?.category) {
    void categoryOptions.splice(0, 1);
  }

  return (
    <div className={styles.wrapper}>
      {showMenu && (
        <DropdownMenuButton open={openMenu} handleClick={() => setOpenMenu(!openMenu)} hoverBlue={hoverBlue} />
      )}
      {openMenu && (
        <DropdownMenu onClose={() => setOpenMenu(false)}>
          {canEditHomePlan && (
            <Link
              onClick={() => {
                modalState.setOpenEditModal(true);
                setActiveHomePlan(homePlan);
                setModalScenario(null);
                setOpenMenu(false);
              }}
            >
              <IconEditSquare size={14} />
              Edit Plan Details
            </Link>
          )}
          {canEditHomePlan && !homePlan.lockedAt && (
            <LockHomePlanButton
              handleClick={() => homePlanLockable && toggleModalScenario("lock")}
              lockable={homePlanLockable}
              tooltip={lockTooltip}
            />
          )}
          {canEditHomePlan && !!homePlan.lockedAt && (
            <Link
              disabled={!homePlanUnlockable}
              tooltip={unlockTooltip}
              onClick={() => homePlanUnlockable && toggleModalScenario("unlock")}
            >
              <IconLocked variant="portal" size={14} />
              Unlock Home Plan
            </Link>
          )}
          {homePlanDetachable && (
            <Link
              disabled={atMultiUnitLocation}
              tooltip={atMultiUnitLocation ? DETACH_MULTI_UNIT_HOME_PLAN_TOOLTIP : undefined}
              onClick={() => toggleModalScenario("detach")}
            >
              <IconDetached size={14} />
              Detach Home Plan
            </Link>
          )}
          {enabledFeatures.homePlanVersions && canEditHomePlan && (
            <Link onClick={() => toggleModalScenario("refreshOptions")}>
              <IconSync size={14} />
              Refresh Option Data
            </Link>
          )}
          <Link
            onClick={() => {
              toggleModalScenario("customerInfo");
              setOpenMenu(false);
            }}
          >
            <IconAddressBook size={14} />
            {hasPermission("EDIT_CUSTOMER_INFO") ? "Edit " : "View "} Customer Info
          </Link>
          <Link
            disabled={!canMoveHomePlan}
            className={styles.centerLink}
            tooltip={!canMoveHomePlan ? cantMoveReason : undefined}
            onClick={() => {
              setOpenMenu(false);
              setModalScenario("move");
            }}
          >
            <IconLineArrow size={12} />
            Move Home Plan
          </Link>
          {canViewStudio && (
            <Link href={studioPath(homePlan.studioDocId, builderId)}>
              <IconFileOpen size={14} />
              Open Studio File
            </Link>
          )}
          {canEditHomePlan && (
            <PopoverMenu
              innerText="Categorize"
              menuItems={categoryOptions}
              position="left"
              onClose={() => setOpenMenu(false)}
              disabled={homePlan?.planStatus !== "PREP"}
            />
          )}
          {canDeleteHomePlan && (
            <DeleteHomePlanMenuLink
              homePlan={homePlan}
              onClick={() => {
                setOpenMenu(false);
                toggleModalScenario("delete");
              }}
            />
          )}
          {(canEditHomePlan || canViewProcure) && <hr />}
          {canEditHomePlan && canViewOperationsMode && (
            <Link
              key="operations_view"
              href={homePlanOpsViewPath(builderId, homePlan.id)}
              disabled={atMultiUnitLocation}
              tooltip={atMultiUnitLocation ? CONFIGURE_OPERATIONS_MULTI_UNIT_TOOLTIP : undefined}
            >
              <IconManagementView size={14} /> Configure in Operations
            </Link>
          )}
          <ShowroomMenuLink key="showroom" builderId={builderId} homePlan={homePlan} canEdit={canEditHomePlan} />
          {canViewProcure && (
            <Link
              key="procure_view"
              href={procurePath({
                builderId,
                homePlanId: homePlan.id,
              })}
            >
              <IconDocSpreadsheet size={14} /> View in Estimate
            </Link>
          )}
        </DropdownMenu>
      )}
      {(() => {
        switch (modalScenario) {
          case "customerInfo":
            return (
              <CustomerInfoModal
                homePlanId={homePlan.id}
                customerId={getHomePlanCustomerId(homePlan)}
                onUpdate={onUpdate}
                setOpen={() => toggleModalScenario("customerInfo")}
                open
              />
            );
          case "move":
            return canMoveHomePlan && <MoveHomePlanModal homePlan={homePlan} onClose={() => setModalScenario(null)} />;
          case "detach":
            return (
              <DetachHomePlanModal
                open
                setOpen={() => toggleModalScenario("detach")}
                builderId={builderId}
                homePlan={homePlan}
                onSuccess={onUpdate}
              />
            );
          case "delete":
            return (
              <ConfirmDeleteModal
                onClose={() => toggleModalScenario("delete")}
                onConfirm={handleSubmitDelete}
                entityType="Home Plan"
                entityName={homePlan.planName ?? "Home Plan"}
                isDeleting={isDeleting}
                message={showMultiUnitDeleteWarning ? DELETE_HOME_PLAN_MULTI_UNIT_MESSAGE : DELETE_HOME_PLAN_MESSAGE}
              />
            );
          case "lock":
            if (!homePlanLockable) {
              return null;
            }
            return (
              <LockPlanModal
                open
                setOpen={() => toggleModalScenario("lock")}
                homePlanId={homePlan.id}
                isLocked={!!homePlan.lockedAt}
                onSuccess={onUpdate}
              />
            );

          case "unlock":
            if (!homePlan.lockedAt || !homePlanUnlockable) {
              return null;
            }
            return (
              <LockPlanModal
                open
                setOpen={() => toggleModalScenario("unlock")}
                homePlanId={homePlan.id}
                isLocked
                onSuccess={onUpdate}
              />
            );
          case "refreshOptions":
            return (
              <RefreshOptionDataModal
                open
                setOpen={() => toggleModalScenario("refreshOptions")}
                builderId={builderId}
                homePlan={homePlan}
                onSuccess={onUpdate}
              />
            );
          default:
            return null;
        }
      })()}
    </div>
  );
}

interface HomePlanDropdownModalsProps {
  builderId: string;
  modalState: HomePlanDropdownModalState;
  onSuccess: (homePlan: ClientHomePlanHydratedNoDoc) => void;
  homePlan: ClientHomePlanHydratedNoDoc | null;
  locationId?: string;
  communityPlan?: ClientLocationPlan;
}

export function HomePlanDropdownModals({
  builderId,
  locationId,
  homePlan,
  modalState,
  communityPlan,
  onSuccess,
}: HomePlanDropdownModalsProps) {
  if (!homePlan) {
    return null;
  }

  return modalState.openEditModal ? (
    <EditHomePlanModal
      homePlan={homePlan}
      onSuccess={onSuccess}
      locationId={locationId}
      builderId={builderId}
      locationPlan={communityPlan}
      onClose={() => modalState.setOpenEditModal(false)}
    />
  ) : null;
}
